import * as React from "react"
import { cn } from "./lib/cn"

const tintStyles = {
  amber: [
    "border-amber-200/20 bg-amber-50/30 backdrop-blur-xs",
    "shadow-[0_1px_3px_rgba(217,158,48,0.06),inset_0_1px_0_rgba(255,255,255,0.4)]",
    "dark:bg-amber-500/6 dark:border-amber-400/10",
  ],
  red: [
    "border-red-200/20 bg-red-50/30 backdrop-blur-xs",
    "shadow-[0_1px_3px_rgba(239,68,68,0.06),inset_0_1px_0_rgba(255,255,255,0.4)]",
    "dark:bg-red-500/6 dark:border-red-400/10",
  ],
  green: [
    "border-emerald-200/20 bg-emerald-50/30 backdrop-blur-xs",
    "shadow-[0_1px_3px_rgba(16,185,129,0.06),inset_0_1px_0_rgba(255,255,255,0.4)]",
    "dark:bg-emerald-500/6 dark:border-emerald-400/10",
  ],
  /**
   * The INVITATION tint (v1.6.0 005 §1.4, README D-U1).
   *
   * The other three are outcomes — amber and red mean something is wrong and
   * fixable, green means it is working. A card that asks the user to do
   * something they have not done yet fits none of them: amber and red would
   * report a fault where there is none, and green already means "syncing" on
   * the surface that needed this, so the two would be indistinguishable at a
   * glance.
   *
   * `rgba(59,130,246,…)` is Tailwind's blue-500 — the same value
   * `GlowIconContainer`'s `blue` glow carries, so the two read as one accent
   * rather than two blues. Structure matches its three siblings line for line
   * (border/bg + one-pixel inset highlight + the dark-mode pair); anything
   * else here is a new visual language, not a fourth member of this one.
   */
  blue: [
    "border-blue-200/20 bg-blue-50/30 backdrop-blur-xs",
    "shadow-[0_1px_3px_rgba(59,130,246,0.06),inset_0_1px_0_rgba(255,255,255,0.4)]",
    "dark:bg-blue-500/6 dark:border-blue-400/10",
  ],
} as const

type TintColor = keyof typeof tintStyles

function TintedGlassCard({
  className,
  tint,
  ...props
}: React.ComponentProps<"div"> & { tint: TintColor }) {
  return (
    <div
      data-slot="tinted-glass-card"
      className={cn(
        "rounded-xl border",
        tintStyles[tint],
        className
      )}
      {...props}
    />
  )
}

export { TintedGlassCard, type TintColor }
